import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  updateEncounterTime,
  fetchStudies,
  fetchPatientConsents,
} from "../../../utils/api";

// Mirrored the effective-consent check used in ConfirmPatientInfo.js so that active consent logic is displayed correct

const isEffectiveActiveStudyConsent = (consent) => {
  if (!consent) return false;
  if (consent.active !== true) return false;
  if (consent.expiresAt) {
    const expiresDate = new Date(consent.expiresAt);
    if (expiresDate <= new Date()) return false;
  }
  if (consent.consentType !== "STUDY_CONSENT") return false;
  if (!consent.irbStudy || consent.irbStudy.trim() === "") return false;
  return true;
};

const EditEncounterModal = ({ open, onClose, encounter, onSave }) => {
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unenrolledStudies, setUnenrolledStudies] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    if (open && encounter) {
      if (encounter.encounterDateTime) {
        try {
          const date = new Date(encounter.encounterDateTime);
          const offset = date.getTimezoneOffset() * 60000;
          const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, 16);
          setNewTime(localISOTime);
        } catch (e) {
          setNewTime("");
        }
      } else {
        setNewTime("");
      }
      setError(null);
      loadUnenrolledStudyBilling(encounter);
    } else if (!open) {
      setUnenrolledStudies([]);
    }
  }, [open, encounter]);

  const loadUnenrolledStudyBilling = async (enc) => {
    if (!enc?.patientId) {
      setUnenrolledStudies([]);
      return;
    }
    try {
      setBillingLoading(true);
      const [allStudies, patientConsents] = await Promise.all([
        fetchStudies(),
        fetchPatientConsents(enc.patientId),
      ]);

      const notYetEnrolled = (allStudies || []).filter((study) => {
        const hasEffectiveConsent = (patientConsents || []).some(
          (c) => c.irbStudy === study.irbStudy && isEffectiveActiveStudyConsent(c)
        );
        return !hasEffectiveConsent;
      });

      setUnenrolledStudies(notYetEnrolled);
    } catch (err) {
      console.error("Error loading billing info for unenrolled studies:", err);
      setUnenrolledStudies([]);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newTime) {
      setError("Please select a valid date and time.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const isoTime = new Date(newTime).toISOString();
      await updateEncounterTime(encounter.id, { encounterDateTime: isoTime });
      onSave();
    } catch (err) {
      console.error("Error updating encounter time:", err);
      setError("Failed to update encounter time. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#500000", fontWeight: 600 }}>
        Edit Encounter Time
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <TextField
            label="Encounter Date & Time"
            type="datetime-local"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            disabled={loading}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 1, color: "#500000" }}>
            Billing Info — Studies Not Yet Enrolled
          </Typography>

          {billingLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading billing info...
              </Typography>
            </Box>
          ) : unenrolledStudies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This participant is enrolled in all available studies.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {unenrolledStudies.map((study) => (
                <Box
                  key={study.irbStudy}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                    {study.irbStudy}: {study.studyName || study.studyShortName || study.irbStudy}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Billing Code: {study.billingCode || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rate: {study.billingRate || study.billingNotes || "N/A"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: "#666" }}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || !newTime}
          sx={{ backgroundColor: "#500000", "&:hover": { backgroundColor: "#3a0000" } }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEncounterModal;